import { useState, useEffect } from 'react';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { EditPage, EditPagePanel, EditPageTextField, EditPageSelect, EditPageCheckbox } from './../../../common/EditPage';
import { useParams, useNavigate } from 'react-router-dom';
import { getOne, update } from './../../../common/v4/BackendProvider';
import { toast } from 'react-toastify';
import Select from 'react-select';

const AdminUsersEdit = () => {
    const [data, setData] = useState({
        name: '',
        email: '',
        password: '',
        phone: '',
        timezone: 'UTC',
        enabled: true,
        skip_email_verification: true,
        webrtc_enable: false,
        webrtc_username: '',
        webrtc_password: '',
        access_group_ids: []
    });
    const [metadata, setMetadata] = useState({});
    const navigate = useNavigate();
    const { userId } = useParams();
    
    useEffect(() => {
        getOne('/admin/users/', userId).then(response => {
            setMetadata(response.data.metadata);
            
            // Transform the data for the form
            const userData = response.data.data;
            setData({
                name: userData.name || '',
                email: userData.email || '',
                password: '', // Password field is empty for editing
                phone: userData.phone || '',
                timezone: userData.timezone || 'UTC',
                enabled: userData.enabled ?? true,
                skip_email_verification: userData.email_verified_at !== null,
                webrtc_enable: userData.webrtc_enable ?? false,
                webrtc_username: userData.webrtc_username || '',
                webrtc_password: userData.webrtc_password || '',
                access_group_ids: userData.access_groups ? userData.access_groups.map(group => group.id) : []
            });
        }).catch(error => {
            toast.error("Error fetching admin user: " + error.message, {});
        });
    }, [userId]);

    const handleSave = () => {
        // Validate required fields
        if (!data.name || !data.email) {
            toast.error("Name and email are required", {});
            return;
        }

        // Validate password length if provided
        if (data.password && data.password.length < 8) {
            toast.error("Password must be at least 8 characters long", {});
            return;
        }

        // Remove password from data if it's empty (to avoid overwriting with empty string)
        const updateData = { ...data };
        if (!updateData.password) {
            delete updateData.password;
        }

        update('/admin/users/', userId, {data: updateData}).then(() => {
            toast.success("Admin user updated successfully", {});
            navigate('/admin/users');
        }).catch(error => {
            toast.error("Error updating admin user: " + (error?.response?.data?.message || error.message), {});
        });
    };

    const handleCancel = () => {
        navigate('/admin/users');
    };

    const onChange = (id, value) => {
        setData({...data, [id]: value});
    };

    const handleAccessGroupsChange = (selectedOptions) => {
        const groupIds = selectedOptions ? selectedOptions.map(option => option.value) : [];
        setData({...data, access_group_ids: groupIds});
    };

    // Prepare access group options for react-select
    const accessGroupOptions = metadata?.access_groups?.map(group => ({
        value: group.id,
        label: group.name,
        description: group.description
    })) || [];

    // Prepare timezone options from backend metadata
    const timezoneOptions = metadata?.timezones?.values ? 
        Object.entries(metadata.timezones.values).map(([key, value]) => ({ value: key, label: value })) :
        [{ value: 'UTC', label: 'UTC' }]; // Fallback to just UTC if metadata not loaded

    return (
        <>
            <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                closeOnClick={true}
                pauseOnHover={true}
                draggable={true}
                progress="undefined"
            />
            <EditPage handleSave={ handleSave } handleCancel={ handleCancel } title="Edit Admin User">
                <EditPagePanel className="col-lg-6">
                    <EditPageTextField 
                        label="Name" 
                        id="name" 
                        onChange={ onChange } 
                        value={ data.name }
                        required
                    />
                    <EditPageTextField 
                        label="Email" 
                        id="email" 
                        onChange={ onChange } 
                        value={ data.email }
                        type="email"
                        required
                    />
                    <EditPageTextField 
                        label="Password" 
                        type="password" 
                        id="password" 
                        onChange={ onChange } 
                        value={ data.password }
                        placeholder="Leave blank to keep current password"
                    />
                </EditPagePanel>
                <EditPagePanel className="col-lg-6">
                    <EditPageTextField 
                        label="Phone" 
                        id="phone" 
                        onChange={ onChange } 
                        value={ data.phone }
                    />
                    <EditPageSelect 
                        label="Timezone" 
                        id="timezone" 
                        onChange={ onChange } 
                        value={ data.timezone } 
                        options={ timezoneOptions.reduce((acc, opt) => {
                            acc[opt.value] = opt.label;
                            return acc;
                        }, {}) }
                    />
                    <div style={{ marginBottom: '1rem' }}>
                        <label style={{ 
                            display: 'block', 
                            marginBottom: '0.5rem', 
                            fontWeight: '600',
                            fontSize: '0.875rem'
                        }}>
                            Access Groups
                        </label>
                        <Select
                            isMulti
                            options={accessGroupOptions}
                            value={accessGroupOptions.filter(option => 
                                data.access_group_ids.includes(option.value)
                            )}
                            onChange={handleAccessGroupsChange}
                            placeholder="Select access groups..."
                            isClearable
                            isSearchable
                            formatOptionLabel={(option) => (
                                <div>
                                    <div style={{ fontWeight: '500' }}>{option.label}</div>
                                    {option.description && (
                                        <div style={{ 
                                            fontSize: '0.875rem', 
                                            color: '#666',
                                            fontStyle: 'italic'
                                        }}>
                                            {option.description}
                                        </div>
                                    )}
                                </div>
                            )}
                        />
                    </div>
                    <EditPageCheckbox 
                        label="Enabled" 
                        id="enabled" 
                        onChange={ onChange } 
                        value={ data.enabled } 
                    />
                    <EditPageCheckbox 
                        label="Do not require email verification" 
                        id="skip_email_verification" 
                        onChange={ onChange } 
                        value={ data.skip_email_verification } 
                    />
                </EditPagePanel>
                
                <EditPagePanel className="col-lg-6">
                    <h4 style={{ marginBottom: '1rem', color: '#333' }}>WebRTC Settings</h4>
                    <EditPageCheckbox 
                        label="Enable WebRTC" 
                        id="webrtc_enable" 
                        onChange={ onChange } 
                        value={ data.webrtc_enable } 
                    />
                    {data.webrtc_enable && (
                        <>
                            <EditPageTextField 
                                label="WebRTC Username" 
                                id="webrtc_username" 
                                onChange={ onChange } 
                                value={ data.webrtc_username }
                                placeholder="Enter WebRTC username"
                            />
                            <EditPageTextField 
                                label="WebRTC Password" 
                                type="password" 
                                id="webrtc_password" 
                                onChange={ onChange } 
                                value={ data.webrtc_password }
                                placeholder="Enter WebRTC password"
                            />
                        </>
                    )}
                </EditPagePanel>
            </EditPage>
        </>
    );
};

export default AdminUsersEdit;
